import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { setCookieConsent, togglePersonalization } from '../redux/cookie/cookieConsentSlice';
import { getCookies, postCookies } from '../services/CookieService';
import { useTranslation } from 'react-i18next';
import '../styles/CookieConsent.css';

const CookieConsentBanner = () => {
    
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const {
    analytics,
    marketing,
    personalization,
    externalMedia,
    isConsentGiven,
    isPersonalizing
  } = useSelector((state) => state.cookieConsent);

  const [localConsent, setLocalConsent] = useState({
    analytics,
    marketing,
    personalization,
    externalMedia,
  });
    
  // Sync local state when personalization starts
  useEffect(() => {
    if (isPersonalizing) {
      setLocalConsent({ analytics, marketing, personalization, externalMedia });
    }
  }, [isPersonalizing]);

  if (isConsentGiven) return null;

  const handleAcceptAll = () => {
    const data = { analytics: true, marketing: true, personalization: true, externalMedia: true };
    dispatch(setCookieConsent(data));
    getCookies(data);
  };

  const handleRejectAll = () => {
    const data = { analytics: false, marketing: false, personalization: false, externalMedia: false };
    dispatch(setCookieConsent(data));
    postCookies(data);
  };

  const handleCheckboxChange = (key) => {
    setLocalConsent((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSavePreferences = () => {
    dispatch(setCookieConsent(localConsent));
    postCookies(localConsent);
    dispatch(togglePersonalization());
  };

  return (
    <AnimatePresence>
      <motion.div
        className="cookie-banner material"
        initial={{ y: '100%', opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: '100%', opacity: 0 }}
        transition={{ type: 'spring', stiffness: 60, damping: 12 }}
      >
        <div className="cookie-inner">
          <h5 className="mb-2">{t('cookie.title')}</h5>
          <p className="text-muted small">{t('cookie.description')}</p>

          {!isPersonalizing ? (
            <>
              <div className="cookie-buttons mt-3">
                <button className="btn btn-outline-primary" onClick={handleAcceptAll}>{t('cookie.acceptAll')}</button>
                <button className="btn btn-outline-secondary" onClick={handleRejectAll}>{t('cookie.onlyNecessary')}</button>
                <button className="btn btn-text mt-2" onClick={() => dispatch(togglePersonalization())}>
                  {t('cookie.customize')}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="cookie-preferences mt-3">
                {[
                  { id: 'essentials', label: t('cookie.essentials').split('\n').map((line, index) => (<span key={index}>{line}</span>)) },
                  { id: 'analytics', label: t('cookie.analytics').split('\n').map((line, index) => (<span key={index}>{line}</span>)) },
                  { id: 'marketing', label: t('cookie.marketing').split('\n').map((line, index) => (<span key={index}>{line}</span>)) },
                  { id: 'personalization', label: t('cookie.personalization').split('\n').map((line, index) => (<span key={index}>{line}</span>)) },
                  { id: 'externalMedia', label: t('cookie.externalMedia').split('\n').map((line, index) => (<span key={index}>{line}</span>)) }
                ].map(({ id, label }) => (
                  <div key={id} className="form-switch">
                    {
                        (id !== 'essentials') ? (
                            <input
                                type="checkbox"
                                checked={localConsent[id]}
                                className='cutom-checkbox'
                                onChange={() => handleCheckboxChange(id)}
                            />
                        ):(
                            <input
                                type="checkbox"
                                checked= 'true'
                                className='cutom-checkbox disabled'
                                disabled
                            />
                        )
                    }
                    <span className="slider" />
                    <label>{label}</label>
                  </div>
                ))}
              </div>

              <div className="cookie-buttons mt-3">
                <button className="btn btn-sm btn-outline-primary btn-save" onClick={handleSavePreferences}>{t('cookie.save')}</button>
                <button className="btn btn-text" onClick={() => dispatch(togglePersonalization())}>
                    {t('cookie.back')}
                </button>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieConsentBanner;
